import { ComponentId } from 'pages/reporting/reports/rug/types'
import {
  GeckoComponentRect,
  GeckoComponentSpace,
  GeckoMeasurementUnit,
  Sectionable,
} from 'types'

/**
 * Creates GeckoComponentRect objects for each section in a Sectionable object.
 *
 * This function converts the sections of a Sectionable (e.g., tank shell courses or
 * boiler walls) into individual rectangular bounds that can be used for data analysis,
 * CDF filtering, or visualization clipping.
 *
 * The function handles both X-split and Y-split sectionables:
 * - For X-split sectionables (e.g., boiler walls): sections define X ranges,
 *   component bounds provide Y range
 * - For Y-split sectionables (e.g., tank shell courses): sections define Y ranges,
 *   component bounds provide X range
 *
 * All output rectangles are normalized to meters for consistency.
 *
 * @param componentId - The ID of the component that the sections belong to
 * @param sectionable - The sectionable object containing sections and split configuration
 * @param componentBounds - The overall bounds of the component (optional, returns empty array if undefined)
 * @returns An array of GeckoComponentRect objects, one for each section, in meters. Preserves original section order.
 *
 * @example
 * ```typescript
 * // For a vertical tank shell with courses (Y-split)
 * const sectionable: Sectionable = {
 *   splitByX: false,
 *   sections: [
 *     { name: "Course 1", min: measurement(0, "m"), max: measurement(5, "m") },
 *     { name: "Course 2", min: measurement(5, "m"), max: measurement(10, "m") }
 *   ],
 *   // ... other properties
 * }
 *
 * const bounds = getBoundsFromSectionable("tank-123", sectionable, componentBounds)
 * // Returns two rectangles: one for each course spanning the full X range
 * ```
 */
export const getBoundsFromSectionable = (
  componentId: ComponentId,
  sectionable: Sectionable,
  componentBounds: GeckoComponentRect | undefined
): GeckoComponentRect[] => {
  if (!componentBounds) return []

  const sections = sectionable.sections
  const metersSpace = GeckoComponentSpace.CreateFromMeasurementUnits(
    componentId,
    GeckoMeasurementUnit.Meters,
    GeckoMeasurementUnit.Meters
  )
  const componentBoundsInMeters = componentBounds.project(metersSpace)

  return sections.map((section) => {
    const min = section.min
    const max = section.max
    return sectionable.splitByX
      ? GeckoComponentRect.CreateFromXYs(
          componentId,
          min.toNumber(GeckoMeasurementUnit.Meters),
          componentBoundsInMeters.y.min.toNumber(GeckoMeasurementUnit.Meters),
          max.toNumber(GeckoMeasurementUnit.Meters),
          componentBoundsInMeters.y.max.toNumber(GeckoMeasurementUnit.Meters),
          GeckoMeasurementUnit.Meters,
          GeckoMeasurementUnit.Meters
        )
      : GeckoComponentRect.CreateFromXYs(
          componentId,
          componentBoundsInMeters.x.min.toNumber(GeckoMeasurementUnit.Meters),
          min.toNumber(GeckoMeasurementUnit.Meters),
          componentBoundsInMeters.x.max.toNumber(GeckoMeasurementUnit.Meters),
          max.toNumber(GeckoMeasurementUnit.Meters),
          GeckoMeasurementUnit.Meters,
          GeckoMeasurementUnit.Meters
        )
  })
}
